import './echo';
import AOS from 'aos';
import 'aos/dist/aos.css';
import Lenis from 'lenis';
import { animate, stagger, inView } from 'motion';
import gsap from 'gsap';
import intersect from '@alpinejs/intersect';
import sort from '@alpinejs/sort';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.css';
import { researchStepMap } from './research/step-map.js';

import { floatingPanel } from './ui/floating-panel';
import { hostFor, isTeleportedClone, pinToViewport, reattach } from './ui/teleported-panel';
import { boardCarousel, splitPane } from './ui/board';
import { cardMenu } from './ui/card-menu';
import { rangeFill } from './ui/range-fill';
import { codeScanner } from './ui/code-scanner';
marked.setOptions({ gfm: true, breaks: true });

/* Self-hosted TinyMCE (GPL), lazy-loaded only when a rich text field mounts so
   its ~1.5MB doesn't ship on every page. Skin/content CSS are imported raw so
   Vite's CSS minifier (lightningcss) doesn't choke on TinyMCE's modern selectors;
   the UI skin is injected once into <head>, the content CSS feeds content_style. */
let tinyPromise = null;
function loadTinymce() {
    if (tinyPromise) {
        return tinyPromise;
    }
    tinyPromise = (async () => {
        const { default: tinymce } = await import('tinymce/tinymce');
        await Promise.all([
            import('tinymce/models/dom'),
            import('tinymce/themes/silver'),
            import('tinymce/icons/default'),
            import('tinymce/plugins/lists'),
            import('tinymce/plugins/link'),
            import('tinymce/plugins/autolink'),
            import('tinymce/plugins/autoresize'),
            import('tinymce/plugins/table'),
            import('tinymce/plugins/searchreplace'),
            import('tinymce/plugins/code'),
        ]);
        const skinCss = (await import('tinymce/skins/ui/oxide/skin.min.css?raw')).default;
        const contentCss = (await import('tinymce/skins/content/default/content.min.css?raw')).default;
        if (!document.getElementById('tinymce-skin')) {
            const style = document.createElement('style');
            style.id = 'tinymce-skin';
            style.textContent = skinCss;
            document.head.appendChild(style);
        }
        const contentStyle =
            contentCss +
            `body{font-family:'Instrument Sans',ui-sans-serif,system-ui,sans-serif;font-size:15px;line-height:1.7;padding:6px 10px;}` +
            `body p{margin:0 0 .75em;} h1,h2,h3{line-height:1.25;}`;
        window.tinymce = tinymce;
        return { tinymce, contentStyle };
    })();
    return tinyPromise;
}

/* Tear down every TinyMCE editor before an SPA navigation removes the DOM —
   otherwise stale editors with detached elements crash the next init's purge
   (purgeDestroyedEditor: parentNode of null). */
document.addEventListener('livewire:navigating', () => {
    if (window.tinymce) {
        try {
            window.tinymce.remove();
        } catch (e) {
            /* ignore */
        }
    }
});

/* Render AI markdown → sanitized HTML. Used both for streamed (live) replies and
   anywhere the client needs to format model output. Never trust raw model HTML. */
window.zaiMarkdown = (text) =>
    DOMPurify.sanitize(marked.parse(text ?? '', { async: false }));

/* Google Maps JS loader (modern importLibrary bootstrap), loaded once on demand.
   The browser key + optional Map ID come from window.ZAR_MAPS, which the
   <x-cms.maps-loader> partial emits only when Maps is enabled + configured. */
let mapsPromise = null;
function loadGoogleMaps() {
    if (mapsPromise) {
        return mapsPromise;
    }
    const cfg = window.ZAR_MAPS;
    if (!cfg || !cfg.key) {
        return Promise.reject(new Error('Google Maps is not configured'));
    }
    mapsPromise = new Promise((resolve) => {
        ((g) => { let h, a, k, p = 'The Google Maps JavaScript API', c = 'google', l = 'importLibrary', q = '__ib__', m = document, b = window; b = b[c] || (b[c] = {}); const d = b.maps || (b.maps = {}), r = new Set(), e = new URLSearchParams(), u = () => h || (h = new Promise(async (f, n) => { a = m.createElement('script'); e.set('libraries', [...r] + ''); for (k in g) e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]); e.set('callback', c + '.maps.' + q); a.src = `https://maps.googleapis.com/maps/api/js?` + e; d[q] = f; a.onerror = () => (h = n(Error(p + ' could not load.'))); a.nonce = m.querySelector('script[nonce]')?.nonce || ''; m.head.append(a); })); d[l] ? console.warn(p + ' only loads once. Ignoring:', g) : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n))); })({
            key: cfg.key,
            v: 'weekly',
            ...(cfg.mapId ? { mapIds: [cfg.mapId] } : {}),
        });
        resolve(window.google);
    });
    return mapsPromise;
}

/* Parse new-Places addressComponents into our Location fields. */
function parseAddressComponents(components) {
    const get = (type) => {
        const c = (components || []).find((x) => (x.types || []).includes(type));
        return c ? (c.longText ?? c.long_name ?? '') : '';
    };
    const streetNo = get('street_number');
    const route = get('route');
    return {
        street: [streetNo, route].filter(Boolean).join(' '),
        city: get('locality') || get('postal_town') || get('sublocality') || '',
        region: get('administrative_area_level_1'),
        postal_code: get('postal_code'),
        country: get('country'),
    };
}

/*
 * Tell any open searchable-select menu that the DOM was just rebuilt under it.
 *
 * A Livewire round trip re-morphs the component, and `x-teleport` reacts by destroying the
 * menu's panel and building a fresh clone back in <body>. Inside a modal that puts it behind
 * the dialog again: the menu looks closed while Alpine still believes it is open, so the next
 * click toggles it shut and it takes two clicks to get back. Pressing "Show more" or typing in
 * the search box both did exactly that.
 *
 * Registered on `livewire:init` rather than inline, because `alpine:init` can run before
 * `window.Livewire` is assigned — and a hook registered against an undefined Livewire is a
 * silent no-op, which is the worst way for this to fail.
 */
document.addEventListener('livewire:init', () => {
    if (window.__selectSearchMorphHook) {
        return;
    }
    window.__selectSearchMorphHook = true;
    // A marker to check in the console when this is suspected of being stale JS:
    // `window.__selectSearchBuild` should say 5. If it is undefined or lower, the page is running an
    // older bundle and no amount of reasoning about the fix will help.
    window.__selectSearchBuild = 5;

    /*
     * A teleported menu is not surplus markup — do not delete it.
     *
     * Morph diffs the live DOM against the HTML the server just sent. A menu parked inside the
     * component's own tree (which a menu inside a modal must be: the dialog's top layer sits
     * above every z-index <body> can offer) is a child the server never rendered, so the diff
     * files it under "left over" and removes it. The panel is always appended LAST, so sparing
     * it costs the diff nothing — the real children still line up one for one.
     *
     * Nothing about this is specific to the searchable select; any x-teleport clone parked in a
     * morphed tree has the same problem. @see ui/teleported-panel.js
     */
    window.Livewire.hook('morph.removing', ({ el, skip }) => {
        if (isTeleportedClone(el)) {
            skip();
        }
    });

    window.Livewire.hook('morphed', () => {
        window.dispatchEvent(new CustomEvent('select-search:remorphed'));
        // The same signal under a neutral name, for everything else that holds inline state
        // a morph can wipe (the floating panels' drag offset, for one).
        window.dispatchEvent(new CustomEvent('zar:morphed'));
    });
});

/* Register Alpine plugins + components onto Livewire's bundled Alpine. */
document.addEventListener('alpine:init', () => {
    window.Alpine.plugin(intersect);
    window.Alpine.plugin(sort);
    window.Alpine.data('researchStepMap', researchStepMap);

    /* Flatpickr-backed date / datetime / time / range picker, synced to a Livewire
       property. Lives in a wire:ignore island (see <x-appointments.date-picker>).
       config: { model, endModel?, mode: 'datetime'|'date'|'time'|'range',
                 minuteIncrement?, minDate?, withTime?, live? } */
    window.Alpine.data('flatpickr', (config = {}) => ({
        fp: null,
        syncing: false,
        reposition: null,
        init() {
            const mode = config.mode || 'datetime';
            const hasTime = mode === 'datetime' || mode === 'time' || (mode === 'range' && config.withTime);
            const format = mode === 'date' ? 'Y-m-d'
                : mode === 'time' ? 'H:i'
                : hasTime ? 'Y-m-d H:i' : 'Y-m-d';

            // Keep the calendar in THIS island's DOM (static → the wire:ignore
            // wrapper) so clicking it counts as inside the modal (no light-dismiss)
            // and survives Livewire morphs. In a modal we then lift it out of the
            // scroll clip with fixed positioning pinned to the input (see place()).
            this.inModal = !!this.$refs.input.closest('dialog');

            this.fp = flatpickr(this.$refs.input, {
                mode: mode === 'range' ? 'range' : 'single',
                enableTime: hasTime,
                noCalendar: mode === 'time',
                dateFormat: format,
                minuteIncrement: config.minuteIncrement ?? 30,
                minDate: config.minDate || null,
                allowInput: false,
                disableMobile: true,
                static: true,
                onChange: (dates) => this.push(dates),
                onOpen: [() => this.onOpen()],
                onClose: [() => this.onClose()],
            });

            this.applyFromWire();
            this.unwatchers = [config.model, config.endModel].filter(Boolean).map(
                (m) => this.$wire.$watch(m, () => this.applyFromWire()),
            );
        },
        // In a modal: lift the calendar out of the scrolling body with fixed
        // positioning pinned to the input, and keep it pinned while open. On a plain
        // page flatpickr's default (static) positioning is already correct.
        onOpen() {
            if (!this.inModal || !this.alive()) {
                return;
            }
            this.reposition = () => this.place();
            this.place();
            window.addEventListener('scroll', this.reposition, true);
            window.addEventListener('resize', this.reposition);
        },
        onClose() {
            if (this.reposition) {
                window.removeEventListener('scroll', this.reposition, true);
                window.removeEventListener('resize', this.reposition);
                this.reposition = null;
            }
            // Drop the inline overrides so static positioning resumes if reopened.
            const cal = this.fp?.calendarContainer;
            if (cal) {
                ['position', 'zIndex', 'margin', 'inset', 'top', 'left'].forEach((p) => {
                    cal.style[p] = '';
                });
            }
        },
        // Pin the calendar to the input via fixed positioning, flipping above when
        // there's no room below. Self-corrects for any transformed ancestor (which
        // would otherwise make fixed coords relative to that ancestor, not the
        // viewport) by measuring where the (0,0) origin actually lands.
        place() {
            if (!this.alive()) {
                return;
            }
            const cal = this.fp.calendarContainer;
            Object.assign(cal.style, {
                position: 'fixed',
                zIndex: '99999',
                margin: '0',
                inset: 'auto',
                top: '0px',
                left: '0px',
            });
            const origin = cal.getBoundingClientRect();
            const rect = this.$refs.input.getBoundingClientRect();
            const calH = cal.offsetHeight || 320;
            const calW = cal.offsetWidth || 307;
            const openUp = window.innerHeight - rect.bottom < calH + 8 && rect.top > calH;
            const desiredTop = openUp ? rect.top - calH - 2 : rect.bottom + 2;
            const desiredLeft = Math.max(8, Math.min(rect.left, window.innerWidth - calW - 8));
            cal.style.top = `${desiredTop - origin.top}px`;
            cal.style.left = `${desiredLeft - origin.left}px`;
        },
        // Stored values may use a "T" separator (datetime-local legacy); flatpickr
        // parses a space.
        norm(value) {
            return (value || '').replace('T', ' ');
        },
        // Guard: a queued $watch can fire after the island's flatpickr was destroyed
        // (Livewire morph / modal close), leaving this.fp without a config.
        alive() {
            return this.fp && this.fp.config;
        },
        fmt(date) {
            return this.alive() ? this.fp.formatDate(date, this.fp.config.dateFormat) : '';
        },
        // Push the picker's selection back to the Livewire property/-ies.
        push(dates) {
            if (this.syncing || !this.alive()) {
                return;
            }
            const live = config.live ?? false;
            if ((config.mode || '') === 'range') {
                // A single date is just the START of a range selection — wait for the
                // second click. Syncing now would re-apply setDate([start]) and reset
                // the picker before the end can be chosen. (length 0 = cleared.)
                if (dates.length === 1) {
                    return;
                }
                this.$wire.set(config.model, dates[0] ? this.fmt(dates[0]) : '', live);
                this.$wire.set(config.endModel, dates[1] ? this.fmt(dates[1]) : '', live);
            } else {
                this.$wire.set(config.model, dates[0] ? this.fmt(dates[0]) : '', live);
            }
        },
        // Reflect an external (server-side) change without re-triggering onChange.
        applyFromWire() {
            if (!this.alive()) {
                return;
            }
            this.syncing = true;
            try {
                if ((config.mode || '') === 'range') {
                    const s = this.norm(this.$wire.get(config.model));
                    const e = this.norm(this.$wire.get(config.endModel));
                    if (s && e) {
                        this.fp.setDate([s, e], false);
                    } else if (s) {
                        this.fp.setDate([s], false);
                    } else {
                        this.fp.clear(false);
                    }
                } else {
                    const v = this.norm(this.$wire.get(config.model));
                    const cur = this.fp.selectedDates[0] ? this.fmt(this.fp.selectedDates[0]) : '';
                    if (v && v !== cur) {
                        this.fp.setDate(v, false);
                    } else if (!v && cur) {
                        this.fp.clear(false);
                    }
                }
            } finally {
                this.syncing = false;
            }
        },
        destroy() {
            this.onClose();
            (this.unwatchers || []).forEach((unwatch) => unwatch?.());
            if (this.fp) {
                this.fp.destroy();
                this.fp = null;
            }
        },
    }));

    /* Google Places address autocomplete (new Places library). On select, fills
       the Location's sibling fields (street/city/region/postal/country + lat/lng/
       place_id) via $wire.set, using the config.fills handle map. */
    window.Alpine.data('addressAutocomplete', (config) => ({
        query: '',
        suggestions: [],
        open: false,
        token: null,
        places: null,
        async init() {
            this.query = this.$wire.get(config.model) || '';
            // Re-anchor the (fixed) dropdown when the page or a scroll container moves
            // so it tracks the input. Capture phase catches the modal body's scroll.
            this._reposition = () => { if (this.open) this.positionDropdown(); };
            window.addEventListener('scroll', this._reposition, true);
            window.addEventListener('resize', this._reposition);
            try {
                await loadGoogleMaps();
                this.places = await window.google.maps.importLibrary('places');
            } catch (e) {
                console.warn('Maps autocomplete unavailable:', e);
            }
        },
        destroy() {
            window.removeEventListener('scroll', this._reposition, true);
            window.removeEventListener('resize', this._reposition);
        },
        /* The suggestions list is rendered `position: fixed` against the input's
           viewport rect so it is never clipped by an ancestor's overflow (e.g. the
           booking modal's scrollable body). Flips above the input near the bottom. */
        positionDropdown() {
            const input = this.$el.querySelector('input');
            const ul = this.$el.querySelector('ul');
            if (!input || !ul) {
                return;
            }
            const r = input.getBoundingClientRect();
            const dropH = Math.min(ul.scrollHeight || 224, 224);
            const flipUp = r.bottom + 4 + dropH > window.innerHeight && r.top - dropH - 4 > 0;
            ul.style.position = 'fixed';
            ul.style.top = `${Math.round(flipUp ? r.top - dropH - 4 : r.bottom + 4)}px`;
            ul.style.left = `${Math.round(r.left)}px`;
            ul.style.width = `${Math.round(r.width)}px`;
            ul.style.zIndex = '10000';
        },
        async search() {
            if (!this.places || this.query.length < 3) {
                this.suggestions = [];
                this.open = false;
                return;
            }
            this.token = this.token || new this.places.AutocompleteSessionToken();
            try {
                const { suggestions } = await this.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    input: this.query,
                    sessionToken: this.token,
                });
                this.suggestions = (suggestions || []).map((s) => ({
                    text: s.placePrediction.text.toString(),
                    placeId: s.placePrediction.toPlace().id,
                }));
                this.open = this.suggestions.length > 0;
                if (this.open) {
                    this.$nextTick(() => this.positionDropdown());
                }
            } catch (e) {
                console.warn('Autocomplete error:', e);
                this.suggestions = [];
            }
        },
        async pick(s) {
            this.query = s.text;
            this.open = false;
            this.suggestions = [];
            try {
                const place = new this.places.Place({ id: s.placeId });
                await place.fetchFields({ fields: ['addressComponents', 'location', 'formattedAddress'] });
                const f = config.fills || {};
                const parts = parseAddressComponents(place.addressComponents || []);
                this.token = null;

                // Drive the sibling fields through their wire:model inputs (set value +
                // dispatch input/blur) so Livewire's own binding batches them into one
                // commit. Sibling fields share this field's model prefix (e.g. "data.").
                const base = config.model.slice(0, config.model.lastIndexOf('.') + 1);
                const fill = (handle, val) => {
                    if (! handle || val === undefined || val === null || val === '') {
                        return;
                    }
                    const el = document.querySelector(`[wire\\:model\\.blur="${base}${handle}"]`)
                        || document.querySelector(`[wire\\:model="${base}${handle}"]`);
                    if (! el) {
                        return;
                    }
                    el.value = val;
                    el.dispatchEvent(new Event('input', { bubbles: true }));
                    el.dispatchEvent(new Event('blur', { bubbles: true }));
                };
                fill(f.address, place.formattedAddress);
                fill(f.street, parts.street);
                fill(f.city, parts.city);
                fill(f.region, parts.region);
                fill(f.postal_code, parts.postal_code);
                fill(f.country, parts.country);
                if (place.location) {
                    fill(f.latitude, place.location.lat());
                    fill(f.longitude, place.location.lng());
                }
                fill(f.place_id, place.id);

                // When asked, push the whole place to a Livewire event instead of
                // (or as well as) filling sibling inputs — one server round-trip sets
                // every field atomically, avoiding the multi-input commit race in
                // dynamically rendered modals.
                if (config.dispatch && window.Livewire) {
                    window.Livewire.dispatch(config.dispatch, {
                        address: place.formattedAddress || this.query,
                        lat: place.location ? place.location.lat() : null,
                        lng: place.location ? place.location.lng() : null,
                        city: parts.city || '',
                    });
                }
            } catch (e) {
                console.warn('Place details error:', e);
            }
        },
    }));

    /* Service areas: Places multi-add storing [{name, place_id, lat, lng}] for
       the location's coverage. Each area's coordinates are captured at selection. */
    window.Alpine.data('serviceAreas', (config) => ({
        items: [],
        query: '',
        suggestions: [],
        open: false,
        token: null,
        places: null,
        async init() {
            const v = this.$wire.get(config.model);
            this.items = Array.isArray(v) ? v : [];
            try {
                await loadGoogleMaps();
                this.places = await window.google.maps.importLibrary('places');
            } catch (e) {
                console.warn('Maps service-areas unavailable:', e);
            }
        },
        async search() {
            if (!this.places || this.query.length < 3) {
                this.suggestions = [];
                this.open = false;
                return;
            }
            this.token = this.token || new this.places.AutocompleteSessionToken();
            try {
                const { suggestions } = await this.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    input: this.query,
                    sessionToken: this.token,
                    includedPrimaryTypes: ['locality', 'administrative_area_level_1', 'administrative_area_level_2', 'postal_code'],
                });
                this.suggestions = (suggestions || []).map((s) => ({
                    text: s.placePrediction.text.toString(),
                    placeId: s.placePrediction.toPlace().id,
                }));
                this.open = this.suggestions.length > 0;
            } catch (e) {
                console.warn('Autocomplete error:', e);
                this.suggestions = [];
            }
        },
        async add(s) {
            try {
                const place = new this.places.Place({ id: s.placeId });
                await place.fetchFields({ fields: ['displayName', 'location', 'formattedAddress'] });
                this.items.push({
                    name: place.displayName || s.text,
                    place_id: place.id,
                    lat: place.location ? place.location.lat() : null,
                    lng: place.location ? place.location.lng() : null,
                });
                this.persist();
                this.query = '';
                this.suggestions = [];
                this.open = false;
                this.token = null;
            } catch (e) {
                console.warn('Place details error:', e);
            }
        },
        remove(i) {
            this.items.splice(i, 1);
            this.persist();
        },
        persist() {
            this.$wire.set(config.model, this.items);
        },
    }));

    /* Rich text field: TinyMCE over a textarea, two-way bound to a Livewire
       property ($model), with a toggle to drop back to a plain textarea. Default
       is the rich editor. The wrapper is wire:ignore so Livewire morphs don't
       destroy the editor; we sync content both ways manually. */
    window.Alpine.data('richText', (model) => ({
        editor: null,
        rich: true,
        init() {
            this.rich = localStorage.getItem('rt.mode') !== 'plain';
            if (this.$refs.ta) {
                this.$refs.ta.value = this.$wire.get(model) ?? '';
            }
            if (this.rich) {
                this.$nextTick(() => this.mount());
            }
            // Reflect external changes (e.g. ✨ AI generate sets the property).
            this.$wire.$watch(model, (value) => {
                const v = value ?? '';
                if (this.editor && this.editor.getContent() !== v) {
                    this.editor.setContent(v);
                }
                if (this.$refs.ta && this.$refs.ta.value !== v) {
                    this.$refs.ta.value = v;
                }
            });
        },
        async mount() {
            if (this.editor) {
                return;
            }
            const { tinymce, contentStyle } = await loadTinymce();
            const el = this.$refs.ta;
            // Bail if the user toggled to plain or the field left the DOM while loading.
            if (!this.rich || this.editor || !el || !document.body.contains(el)) {
                return;
            }
            // Before init, clear editors TinyMCE still tracks whose DOM is gone
            // (left over from a wire:navigate / re-render), plus any editor bound
            // to THIS element. Dead editors get initialized=false first so
            // TinyMCE's init-time purge (purgeDestroyedEditor) can't read
            // parentNode of a null element and crash.
            (tinymce.get() || []).forEach((ed) => {
                const node = ed.getContainer && ed.getContainer();
                // Only treat an INITIALIZED editor with a missing/detached
                // container as dead — leaves editors still mid-init (other fields)
                // untouched.
                const dead = ed.initialized && (!node || !document.body.contains(node));
                if (dead || ed.getElement() === el) {
                    if (dead) {
                        ed.initialized = false;
                    }
                    try {
                        ed.remove();
                    } catch (e) {
                        /* ignore */
                    }
                }
            });
            tinymce.init({
                target: el,
                license_key: 'gpl',
                skin: false,
                content_css: false,
                content_style: contentStyle,
                menubar: false,
                branding: false,
                promotion: false,
                statusbar: false,
                plugins: 'lists link autolink autoresize table searchreplace code',
                toolbar:
                    'undo redo | blocks | bold italic underline | bullist numlist | link table | removeformat | code',
                autoresize_bottom_margin: 20,
                min_height: 260,
                max_height: 640,
                setup: (ed) => {
                    this.editor = ed;
                    ed.on('init', () => ed.setContent(this.$wire.get(model) ?? ''));
                    ed.on('change keyup undo redo SetContent', () =>
                        this.$wire.set(model, ed.getContent(), false)
                    );
                },
            });
        },
        teardown() {
            if (this.editor) {
                try {
                    this.editor.remove();
                } catch (e) {
                    /* element may already be detached */
                }
                this.editor = null;
            }
        },
        toggle() {
            this.rich = !this.rich;
            localStorage.setItem('rt.mode', this.rich ? 'rich' : 'plain');

            // Toggle by SHOWING/HIDING the editor (TinyMCE swaps with the
            // textarea) — never destroy it, so switching back always works.
            if (this.rich) {
                if (this.editor) {
                    // Pull in any edits made in plain mode, then reveal.
                    this.editor.setContent(this.$refs.ta ? (this.$refs.ta.value ?? '') : '');
                    this.editor.show();
                } else {
                    this.$nextTick(() => this.mount());
                }
            } else if (this.editor) {
                // Sync editor → textarea + Livewire, then drop to the textarea.
                this.editor.save();
                this.$wire.set(model, this.editor.getContent(), false);
                this.editor.hide();
            }
        },
        onInput(e) {
            this.$wire.set(model, e.target.value, false);
        },
        destroy() {
            this.teardown();
        },
    }));

    window.Alpine.data('runbookNotepad', () => ({
        x: 48,
        y: 72,
        width: 680,
        height: 620,
        interaction: null,
        init() {
            const saved = JSON.parse(localStorage.getItem('runbook.notepad.window') || 'null');
            if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)
                && Number.isFinite(saved.width) && Number.isFinite(saved.height)) {
                this.x = saved.x;
                this.y = saved.y;
                this.width = saved.width;
                this.height = saved.height;
            }
            this.clamp();
        },
        /**
         * An object, for the same reason zarPanel returns one: a string style binding
         * replaces the whole attribute, so it would erase a `display: none` if this panel
         * were ever shown with x-show rather than a server-side condition.
         */
        get style() {
            return {
                left: `${this.x}px`,
                top: `${this.y}px`,
                width: `${this.width}px`,
                height: `${this.height}px`,
            };
        },
        dragStart(event) {
            if (event.target.closest('button')) {
                return;
            }
            this.start(event, 'drag');
        },
        resizeStart(event) {
            this.start(event, 'resize');
        },
        start(event, mode) {
            event.preventDefault();
            this.interaction = { mode, x: event.clientX, y: event.clientY, left: this.x, top: this.y, width: this.width, height: this.height };
            const move = (moveEvent) => this.move(moveEvent);
            const end = () => {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', end);
                this.interaction = null;
                this.persist();
            };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', end, { once: true });
        },
        move(event) {
            if (!this.interaction) {
                return;
            }
            const deltaX = event.clientX - this.interaction.x;
            const deltaY = event.clientY - this.interaction.y;
            if (this.interaction.mode === 'drag') {
                this.x = this.interaction.left + deltaX;
                this.y = this.interaction.top + deltaY;
            } else {
                this.width = this.interaction.width + deltaX;
                this.height = this.interaction.height + deltaY;
            }
            this.clamp();
        },
        clamp() {
            this.width = Math.min(Math.max(this.width, 320), Math.max(320, window.innerWidth - 16));
            this.height = Math.min(Math.max(this.height, 384), Math.max(384, window.innerHeight - 16));
            this.x = Math.min(Math.max(this.x, 8), Math.max(8, window.innerWidth - this.width - 8));
            this.y = Math.min(Math.max(this.y, 8), Math.max(8, window.innerHeight - this.height - 8));
        },
        persist() {
            localStorage.setItem('runbook.notepad.window', JSON.stringify({
                x: this.x, y: this.y, width: this.width, height: this.height,
            }));
        },
    }));

    /* Tag/category combobox: type to filter existing terms (client-side over a
       JSON list), Enter/click to select, optional "create new". Selection state
       lives server-side (termSelections); bubbles + the term list re-render via
       Livewire, so this component just drives the search dropdown. */
    window.Alpine.data('termCombobox', (config) => ({
        q: '',
        open: false,
        terms: config.terms || [],
        selectedIds: config.selectedIds || [],
        taxonomyId: config.taxonomyId,
        allowCreate: !!config.allowCreate,
        get matches() {
            const q = this.q.trim().toLowerCase();
            let list = this.terms.filter((t) => !this.selectedIds.includes(t.id));
            if (q) {
                list = list.filter((t) => t.label.toLowerCase().includes(q));
            }
            return list.slice(0, 8);
        },
        get exactExists() {
            const q = this.q.trim().toLowerCase();
            return this.terms.some((t) => t.name.toLowerCase() === q);
        },
        get canCreate() {
            return this.allowCreate && this.q.trim().length > 0 && !this.exactExists;
        },
        pick(id) {
            this.$wire.selectTerm(id);
            this.q = '';
            this.open = false;
        },
        create() {
            if (this.canCreate) {
                this.$wire.createTerm(this.taxonomyId, this.q.trim());
                this.q = '';
                this.open = false;
            }
        },
        onEnter() {
            const m = this.matches;
            if (m.length) {
                this.pick(m[0].id);
            } else {
                this.create();
            }
        },
    }));

    /* Generic searchable single-select (the <x-ui.select-search> component). Only
       manages open/search-text + FIXED positioning of the panel so it escapes any
       ancestor overflow clip (e.g. the mapping grid's overflow-x-auto). Options are
       server-rendered and filtered client-side by data-search; picking/clearing is
       an inline $wire.set()/method call in the markup, so there is no wire:ignore
       and no entangled state to go stale across Livewire morphs. */
    /**
     * A draggable, collapsible floating panel that remembers where you put it.
     *
     * Used by the research workspace so the card editor and the note pad sit OVER the mind
     * map instead of squeezing it: you drag them out of the way of whatever you're reading,
     * and they come back where you left them. `key` scopes the remembered position, so two
     * panels don't fight over one slot.
     */
    /**
     * "There is more below this."
     *
     * Watches a sentinel at the end of a panel and offers a jump to it while it is off
     * screen. Deliberately an IntersectionObserver rather than a scroll listener: a handler
     * that runs on every scroll frame reads layout to decide what to show, and reading layout
     * during a scroll is how a page starts to feel heavy — on the one screen where somebody
     * is dragging through eight hundred steps.
     */
    window.Alpine.data('zarScrollHint', () => ({
        more: false,
        init() {
            const end = this.$refs.end;

            // No observer (or nothing to observe) simply means no hint. The content is still
            // reachable by scrolling; the arrow is a courtesy, never the only way down.
            if (! end || ! ('IntersectionObserver' in window)) return;

            this._io = new IntersectionObserver(
                ([entry]) => { this.more = ! entry.isIntersecting; },
                { root: null, threshold: 0 },
            );
            this._io.observe(end);
        },
        destroy() {
            this._io?.disconnect();
        },
        toEnd() {
            this.$refs.end?.scrollIntoView({
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
                block: 'end',
            });
        },
    }));

    window.Alpine.data('floatingPanel', floatingPanel);
    // QR and barcode scanning with the camera (ZAR-141).
    window.Alpine.data('codeScanner', codeScanner);

    window.Alpine.data('zarPanel', (key, fallback = {}) => ({
        x: 0,
        y: 0,
        // Size is opt-in: a panel that passes no w/h keeps whatever CSS gives it, so adding
        // resize here changed nothing for the panels that were already using this.
        w: null,
        h: null,
        min: false,
        drag: false,
        sx: 0,
        sy: 0,
        init() {
            const saved = JSON.parse(localStorage.getItem(`zar-panel-${key}`) || 'null');
            this.x = saved?.x ?? fallback.x ?? Math.max(24, window.innerWidth - 500);
            this.y = saved?.y ?? fallback.y ?? 96;
            this.w = saved?.w ?? fallback.w ?? null;
            this.h = saved?.h ?? fallback.h ?? null;
            this.min = saved?.min ?? false;
            this.clamp();
            if (fallback.keepVisible) this.$nextTick(() => this.clamp());
            this.$watch('min', () => this.remember());
            window.addEventListener('resize', () => this.clamp());
        },
        /**
         * An OBJECT, never a string.
         *
         * Alpine binds a string style with setAttribute('style', ...), which replaces the
         * whole attribute — including the `display: none` that x-show had written onto the
         * same element. Any re-evaluation then un-hides the panel: resize the window until
         * clamp() moves a closed panel by a pixel and it opens itself, with nobody touching
         * it. The object form sets each property individually and leaves display alone.
         *
         * Empty strings rather than omitted keys, so a panel that had a size and lost one
         * has the old value removed instead of left behind.
         */
        style() {
            return {
                left: `${this.x}px`,
                top: `${this.y}px`,
                width: this.w && this.h ? `${this.w}px` : '',
                height: this.w && this.h ? `${this.h}px` : '',
            };
        },
        /**
         * Drag the bottom-right corner. Kept separate from start() because the header drags
         * the panel and the corner sizes it — one pointer gesture, two meanings.
         */
        startResize(event) {
            event.preventDefault();
            event.stopPropagation();

            const startW = this.w ?? this.$el.offsetWidth;
            const startH = this.h ?? this.$el.offsetHeight;
            const sx = event.clientX;
            const sy = event.clientY;
            const minW = fallback.minW ?? 320;
            const minH = fallback.minH ?? 240;

            const move = (e) => {
                this.w = Math.max(minW, Math.min(startW + (e.clientX - sx), window.innerWidth - this.x - 8));
                this.h = Math.max(minH, Math.min(startH + (e.clientY - sy), window.innerHeight - this.y - 8));
            };
            const up = () => {
                this.remember();
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
            };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
        },
        start(event) {
            // Buttons, inputs and anything marked data-no-drag stay interactive.
            if (event.target.closest('button, input, select, textarea, a, [data-no-drag]')) return;
            this.drag = true;
            this.sx = event.clientX - this.x;
            this.sy = event.clientY - this.y;
            const move = (e) => {
                if (!this.drag) return;
                this.x = e.clientX - this.sx;
                this.y = e.clientY - this.sy;
            };
            const up = () => {
                this.drag = false;
                this.clamp();
                this.remember();
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
            };
            window.addEventListener('pointermove', move);
            window.addEventListener('pointerup', up);
        },
        /** Never let a panel be dragged off-screen where it can't be grabbed back. */
        clamp() {
            const visibleWidth = fallback.keepVisible ? (this.w ?? this.$el.offsetWidth) : 120;
            const visibleHeight = fallback.keepVisible ? (this.h ?? this.$el.offsetHeight) : 80;
            this.x = Math.min(Math.max(this.x, 8), Math.max(8, window.innerWidth - visibleWidth - 8));
            this.y = Math.min(Math.max(this.y, 8), Math.max(8, window.innerHeight - visibleHeight - 8));
        },
        remember() {
            localStorage.setItem(`zar-panel-${key}`, JSON.stringify({ x: this.x, y: this.y, w: this.w, h: this.h, min: this.min }));
        },
    }));

    /**
     * The menu builder's drag surface (Sitemap → Menus).
     *
     * WordPress's gesture: drag up/down to reorder, drag RIGHT to nest under the item above.
     * Sortable gives the reorder; the horizontal distance gives the depth.
     *
     * The server is authoritative. Sortable moves only the row you grabbed — never its
     * children — so its DOM change is undone the instant it lands and the real move is computed
     * from the pure tree module, applied server-side, and re-rendered. Without that undo, a
     * parent visibly separates from its children for a round trip.
     *
     * Both libraries load on demand: this is one page, and neither belongs in the bundle every
     * other page pays for.
     */
    window.Alpine.data('menuBuilder', () => ({
        sortable: null,
        tree: null,
        startX: 0,
        lastX: 0,
        startDepth: 0,
        track: null,
        // The dragged row's next sibling BEFORE the drag — the one reference that puts it
        // back exactly where it was, whichever direction it travelled. Index arithmetic here
        // was the old bug: children[oldIndex] is one row late after an upward drag, so the
        // "restored" DOM was wrong and the order sent to the server described a different
        // row than the one the user dragged.
        anchor: null,

        async init() {
            const [{ default: Sortable }, tree] = await Promise.all([
                import('sortablejs'),
                import('./menus/tree.js'),
            ]);
            this.tree = tree;

            this.sortable = Sortable.create(this.$refs.list, {
                handle: '[data-menu-handle]',
                draggable: '[data-key]',
                animation: 160,
                easing: 'cubic-bezier(0.2, 0, 0, 1)',
                // The GHOST is the landing zone — Sortable moves it to wherever the row would
                // drop, so styling it as a dashed outline turns "somewhere around here" into a
                // visible slot. The row under the cursor is dimmed separately.
                ghostClass: 'rb-drop-zone',
                chosenClass: 'rb-dragging',
                dragClass: 'rb-drag-image',
                fallbackOnBody: true,
                onStart: (evt) => this.onStart(evt),
                onEnd: (evt) => this.onEnd(evt),
            });
        },

        destroy() {
            this.stopTracking();
            this.sortable?.destroy();
            this.sortable = null;
        },

        onStart(evt) {
            this.startDepth = Number(evt.item.dataset.depth || 0);
            this.anchor = evt.item.nextElementSibling;
            this.startX = this.lastX = 0;
            // Sortable's own events don't carry a usable X on every input type, so follow the
            // pointer directly for the length of the drag.
            this.track = (e) => {
                const x = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
                if (this.startX === 0) { this.startX = x; }
                this.lastX = x;
            };
            document.addEventListener('pointermove', this.track, { passive: true });
            document.addEventListener('touchmove', this.track, { passive: true });
        },

        onEnd(evt) {
            this.stopTracking();

            const { oldIndex, newIndex } = evt;
            // Put the DOM back — before its pre-drag next sibling, which is exact in both
            // directions — so the rows read below are the SAME list the server holds.
            // Livewire then re-renders the real order from the server's answer.
            const list = evt.from;
            list.insertBefore(evt.item, this.anchor && this.anchor.parentNode === list ? this.anchor : null);
            this.anchor = null;

            if (oldIndex === undefined || newIndex === undefined) { return; }

            const wanted = this.tree.depthFromOffset(this.startDepth, this.lastX - this.startX);
            // A drop back where it started with no depth change is not an edit — and with
            // every edit now saved immediately, treating it as one would curate the menu
            // for touching a handle.
            if (oldIndex === newIndex && wanted === this.startDepth) { return; }

            const rows = Array.from(list.querySelectorAll('[data-key]')).map((el) => ({
                key: el.dataset.key,
                depth: Number(el.dataset.depth || 0),
            }));

            const next = this.tree.move(rows, oldIndex, this.tree.dropSlot(oldIndex, newIndex), wanted);

            this.$wire.call('reorder', this.tree.toOrder(next));
        },

        stopTracking() {
            if (! this.track) { return; }
            document.removeEventListener('pointermove', this.track);
            document.removeEventListener('touchmove', this.track);
            this.track = null;
        },
    }));

    /**
     * Kanban drag (research board's lane view). One Sortable per lane, one shared group.
     *
     * Same contract as the menu builder: the server is authoritative. Sortable's DOM move is
     * undone the moment it lands and the real change — "this card, that lane" — goes through
     * Livewire, which re-renders from what actually got written (a locked card's refusal
     * arrives as a toast and the card stays put).
     */
    /**
     * Which phases of the authoring board are open.
     *
     * A runbook with a dozen phases drew a dozen full four-column boards, which is a page you
     * scroll past rather than read. Only the first opens by default — a board showing nothing
     * at all looks broken — and the choice is remembered per runbook, because somebody who
     * collapsed eleven of them did it for a reason that outlives the page load.
     */
    /**
     * Which phases of the authoring board are open.
     *
     * The SERVER owns this, because it decides which phases to build cards for — a closed
     * phase is not hidden, it is never rendered. This mirrors the answer into localStorage so
     * the choice survives a visit, and tells the server once on load when it remembers
     * something different from the default.
     */
    window.Alpine.data('boardSwimlaneState', (key, ids = [], openNow = []) => ({
        init() {
            let saved = null;
            try {
                saved = JSON.parse(localStorage.getItem(`zar-board-${key}`) || 'null');
            } catch (e) {
                saved = null;
            }

            if (! Array.isArray(saved)) return;

            // Only phases that still exist; a remembered id for a deleted phase is noise.
            const wanted = saved.filter((id) => ids.includes(id));
            const same = wanted.length === openNow.length && wanted.every((id) => openNow.includes(id));

            if (! same) {
                this.$wire?.call?.('setOpenBoardPhases', wanted);
            }
        },

        toggle(id) {
            /*
             * The server owns the answer; this only writes it down. `openNow` is what the
             * server said when this component last rendered, so the next set is computed from
             * that rather than from anything kept here — a local copy drifting from the server
             * is how a toggle starts needing two presses.
             */
            const next = openNow.includes(id) ? openNow.filter((x) => x !== id) : [...openNow, id];

            try {
                localStorage.setItem(`zar-board-${key}`, JSON.stringify(next));
            } catch (e) {
                // A browser refusing storage is not a reason to refuse the toggle.
            }

            this.$wire?.call?.('toggleBoardPhase', id);
        },
    }));

    /**
     * The tuning every kanban on the app shares.
     *
     * Extracted rather than copied because it is not a set of preferences — every line in
     * it is the fix for something that went wrong (a native drag image that cannot be
     * styled, a card that loses its Alpine scope on <body>, a scroll gesture that picks a
     * card up). A second board copying it would copy the values and lose the reasons, and
     * would then drift the first time one of them needed changing.
     *
     * The CONTRACT is shared too: the server is authoritative. Sortable's DOM move is put
     * back the moment it lands, and the real change goes through Livewire, which re-renders
     * from whatever actually got written.
     */
    const kanbanDragOptions = {
        group: 'kanban',
        draggable: '[data-kanban-card]:not([data-kanban-disabled])',

        /*
         * The ghost is the placeholder Sortable leaves where the card would land,
         * and by default it is a faded copy of the card — which tells you what you
         * are dragging, something you already know, rather than WHERE it is going.
         * Styled as an insertion line instead. @see .rb-drop-line
         */
        ghostClass: 'rb-drop-line',

        /** The card under the cursor, so it reads as lifted rather than duplicated. */
        dragClass: 'rb-dragging',
        chosenClass: 'rb-chosen',
        // The clone that follows the cursor. It is measured FROM the placeholder,
        // which by then is a 2px line — so it needs its own size back. @see app.css
        fallbackClass: 'rb-fallback',

        animation: 120,
        easing: 'cubic-bezier(0.2, 0, 0, 1)',
        // Halfway across a card is enough to mean "past it". The default asks for
        // most of the card and makes short lists feel sticky.
        swapThreshold: 0.65,
        /*
         * Sortable's own drag rather than the browser's. Native HTML5 drag hands
         * the browser a screenshot of the element and ignores dragClass entirely,
         * so the card being moved cannot be styled — and the native drag image is
         * a washed-out bitmap that lags the cursor. This is what makes it feel
         * smooth, and it is the same path touch already takes.
         */
        forceFallback: true,
        fallbackTolerance: 4,

        /*
         * NOT on the body.
         *
         * Sortable's fallback drag moves the element itself, and fallbackOnBody
         * moves it to <body> — out of the Livewire component, and so out of the
         * Alpine scope that `rowMenu` lives in. Alpine re-initialises the moved
         * node, every binding on the card that reads root state throws
         * "rowMenu is not defined", and the throw lands inside Sortable's own
         * drag-start: the drag then stutters and dies mid-move.
         *
         * Kept in its container, the card keeps its scope and the drag completes.
         */
        fallbackOnBody: false,
        // A touch has to settle before it is a drag, or scrolling the board picks
        // a card up instead of moving the page.
        delay: 60,
        delayOnTouchOnly: true,
        touchStartThreshold: 6,
    };

    window.Alpine.data('kanbanLanes', () => ({
        sortables: [],

        async init() {
            const { default: Sortable } = await import('sortablejs');

            this.$el.querySelectorAll('[data-kanban-lane]').forEach((lane) => {
                this.sortables.push(Sortable.create(lane, {
                    ...kanbanDragOptions,

                    onEnd: (evt) => {
                        const entryId = Number(evt.item.dataset.kanbanCard || 0);
                        const laneValue = evt.to?.dataset.kanbanLane ?? '';
                        // Swimlanes are phases. Crossing one is a structural move, so the
                        // target phase travels with the lane.
                        const phaseId = Number(evt.to?.dataset.kanbanPhase || 0);

                        // Put the DOM back; the server's answer is what renders.
                        const back = evt.from.children[evt.oldIndex] ?? null;
                        evt.from.insertBefore(evt.item, back);

                        if (entryId > 0 && evt.to !== evt.from) {
                            this.$wire.call('moveCard', entryId, laneValue, phaseId);
                        }
                    },
                }));
            });
        },

        destroy() {
            this.sortables.forEach((s) => s.destroy());
            this.sortables = [];
        },
    }));

    /**
     * The tasks board.
     *
     * Same drag, one difference that matters: a drop is reported even when the card did not
     * change column. On a runbook board a lane is a status and the rank inside it means
     * nothing, so a same-lane move is genuinely nothing to save. On a backlog the rank IS the
     * plan — "these three next, in this order" — so every drop carries its new index.
     */
    window.Alpine.data('taskLanes', () => ({
        sortables: [],

        async init() {
            const { default: Sortable } = await import('sortablejs');

            this.$el.querySelectorAll('[data-kanban-lane]').forEach((lane) => {
                this.sortables.push(Sortable.create(lane, {
                    ...kanbanDragOptions,

                    onEnd: (evt) => {
                        const id = Number(evt.item.dataset.kanbanCard || 0);
                        const state = evt.to?.dataset.kanbanLane ?? '';
                        const index = evt.newIndex ?? 0;
                        const moved = evt.to !== evt.from || evt.newIndex !== evt.oldIndex;

                        // Put the DOM back; the server's answer is what renders.
                        const back = evt.from.children[evt.oldIndex] ?? null;
                        evt.from.insertBefore(evt.item, back);

                        if (id > 0 && state !== '' && moved) {
                            this.$wire.call('moveTask', id, state, index);
                        }
                    },
                }));
            });
        },

        destroy() {
            this.sortables.forEach((s) => s.destroy());
            this.sortables = [];
        },
    }));

    // The board's chrome. Its own module so it can be tested against real Alpine — the
    // carousel shipped broken over an `x-ref` that could not cross an x-data boundary, which
    // is a property of the markup rather than of the code. @see ./ui/board.js
    window.Alpine.data('splitPane', (key, initial, min, max) => splitPane(key, initial, min, max));
    window.Alpine.data('boardCarousel', () => boardCarousel());
    window.Alpine.data('rangeFill', (property = null) => rangeFill(property));
    window.Alpine.data('cardMenu', () => cardMenu());

    window.Alpine.data('runbookTree', () => ({
        sortable: null,
        originalNext: null,
        originalRowIds: [],

        async init() {
            const { default: Sortable } = await import('sortablejs');

            this.sortable = Sortable.create(this.$el, {
                draggable: '[data-runbook-row]',
                handle: '[data-runbook-drag-handle]',
                animation: 120,
                easing: 'cubic-bezier(0.2, 0, 0, 1)',
                // The same insertion line the board draws. In a deep tree the seam a row will
                // land in is the one thing a faded copy of the row cannot tell you.
                ghostClass: 'rb-drop-line',
                dragClass: 'rb-dragging',
                chosenClass: 'rb-chosen',
                fallbackClass: 'rb-fallback',
                forceFallback: true,
                fallbackTolerance: 4,
                onStart: (event) => {
                    // Phases sit in blocks separated by a gap. While a row is in flight that
                    // gap would open and close under the cursor as the row passes each phase,
                    // which makes the list jump; the drop line marks position instead.
                    this.$el.classList.add('rb-dragging-tree');

                    this.originalNext = event.item.nextElementSibling;
                    this.originalRowIds = Array.from(this.$el.querySelectorAll('[data-runbook-row]'))
                        .map((row) => Number(row.dataset.runbookRow || 0));
                },
                onEnd: (event) => {
                    this.$el.classList.remove('rb-dragging-tree');

                    // A short pulse where it landed. Sortable's own animation only covers the
                    // rows sliding apart; without this the dropped row is indistinguishable
                    // from the seventy others the moment the cursor lets go.
                    event.item.classList.add('rb-dropped');
                    window.setTimeout(() => event.item.classList.remove('rb-dropped'), 600);

                    const stepId = Number(event.item.dataset.runbookRow || 0);
                    const depth = Number(event.item.dataset.runbookDepth || 0);
                    const rows = Array.from(this.$el.querySelectorAll('[data-runbook-row]'));
                    const rowIds = rows.map((row) => Number(row.dataset.runbookRow || 0));
                    const changed = JSON.stringify(rowIds) !== JSON.stringify(this.originalRowIds);
                    const newIndex = rows.indexOf(event.item);
                    const preceding = rows.slice(0, newIndex).reverse()
                        .find((row) => Number(row.dataset.runbookDepth || 0) <= depth);
                    const following = rows.slice(newIndex + 1)
                        .find((row) => Number(row.dataset.runbookDepth || 0) <= depth);

                    let action = null;
                    let targetId = 0;
                    let placement = 'after';

                    if (preceding && Number(preceding.dataset.runbookDepth || 0) === depth) {
                        action = 'relative';
                        targetId = Number(preceding.dataset.runbookRow || 0);
                    } else if (preceding && Number(preceding.dataset.runbookDepth || 0) === depth - 1) {
                        action = 'into';
                        targetId = Number(preceding.dataset.runbookRow || 0);
                    } else if (following && Number(following.dataset.runbookDepth || 0) === depth) {
                        action = 'relative';
                        targetId = Number(following.dataset.runbookRow || 0);
                        placement = 'before';
                    } else if (following && Number(following.dataset.runbookDepth || 0) === depth - 1) {
                        action = 'into';
                        targetId = Number(following.dataset.runbookRow || 0);
                    }

                    if (this.originalNext?.isConnected) {
                        this.$el.insertBefore(event.item, this.originalNext);
                    } else {
                        this.$el.appendChild(event.item);
                    }

                    if (changed && action === 'relative' && stepId > 0 && targetId > 0 && stepId !== targetId) {
                        this.$wire.call('reorderStep', stepId, targetId, placement);
                    } else if (changed && action === 'into' && stepId > 0 && targetId > 0 && stepId !== targetId) {
                        this.$wire.call('moveStepInto', stepId, targetId);
                    }

                    this.originalNext = null;
                    this.originalRowIds = [];
                },
            });
        },

        destroy() {
            this.sortable?.destroy();
            this.sortable = null;
        },
    }));

    /**
     * The floating team-chat panel.
     *
     * Draggable by its header and remembered per room, so a panel parked out of the way of the
     * step you are reading stays there. The drag is a translate rather than left/top: the panel
     * is anchored bottom-right, and moving it by its anchor would fight the anchor.
     *
     * Typing travels as WINDOW EVENTS, not as its own socket. Whoever owns the presence channel
     * for the surface (the canvas island on research, roomPresence on runbooks) fans whispers
     * out as `room-chat-typing-seen` and listens for `room-chat-typing` — so there is exactly
     * one subscription per page and no second lifecycle to keep in agreement with the first.
     */
    window.Alpine.data('roomChat', (config = {}) => ({
        x: 0,
        y: 0,
        drag: false,
        sx: 0,
        sy: 0,
        typers: {},

        init() {
            const key = `zar-chat-${config.room || 'default'}`;
            const saved = JSON.parse(localStorage.getItem(key) || 'null');
            this.x = saved?.x ?? 0;
            this.y = saved?.y ?? 0;
            this.clamp();

            const remember = () => localStorage.setItem(key, JSON.stringify({ x: this.x, y: this.y }));
            this.$watch('drag', (dragging) => { if (!dragging) remember(); });

            window.addEventListener('resize', () => this.clamp());
            this._prune = window.setInterval(() => this.prune(), 1000);
            this._seen = (event) => this.seen(event.detail);
            window.addEventListener('room-chat-typing-seen', this._seen);

            // The panel re-reads the thread; the socket only ever says that something changed.
            if (config.socket && window.Echo && config.room) {
                window.Echo.private(`chat-room.${config.room}`)
                    .listen('.room.message', () => this.$wire?.call?.('refreshRoomChat'));
            }
        },

        destroy() {
            window.clearInterval(this._prune);
            window.removeEventListener('room-chat-typing-seen', this._seen);
            if (config.socket && config.room) window.Echo?.leave?.(`chat-room.${config.room}`);
        },

        start(event) {
            if (event.target.closest('button, textarea, input, select, a, [data-no-drag]')) return;
            this.drag = true;
            this.sx = event.clientX - this.x;
            this.sy = event.clientY - this.y;
        },

        move(event) {
            if (!this.drag) return;
            this.x = event.clientX - this.sx;
            this.y = event.clientY - this.sy;
        },

        drop() {
            if (!this.drag) return;
            this.drag = false;
            this.clamp();
        },

        /** Never let the panel be dragged off-screen where it cannot be grabbed back. */
        clamp() {
            this.x = Math.min(Math.max(this.x, -(window.innerWidth - 120)), 120);
            this.y = Math.min(Math.max(this.y, -(window.innerHeight - 160)), 80);
        },

        typing() {
            window.dispatchEvent(new CustomEvent('room-chat-typing'));
        },

        seen(detail) {
            if (!detail || !detail.userId) return;
            this.typers[detail.userId] = { name: detail.name || 'Someone', at: Date.now() };
        },

        /** A colleague who wandered off mid-thought must not type forever. */
        prune() {
            const now = Date.now();
            for (const id in this.typers) {
                if (now - this.typers[id].at > 3000) delete this.typers[id];
            }
        },

        typingLabel() {
            const names = Object.values(this.typers).map((t) => t.name);
            if (!names.length) return '';
            return names.length === 1 ? `${names[0]} is typing` : `${names.join(' and ')} are typing`;
        },
    }));

    /**
     * Who else is on this surface, and what they are looking at.
     *
     * A registered component rather than an inline x-data expression, because an x-data
     * attribute is HTML: the first double quote inside it — in a string, or in a COMMENT —
     * ends the attribute and the rest of the component becomes stray markup. That failure is
     * silent in Blade and only shows up as "Unexpected token )" in the console.
     *
     * Identity comes from the presence channel itself (name and colour, nothing else); focus
     * travels by whisper, because "Ana is on step 2.3" is worth showing and not worth a write.
     */
    /**
     * Tell the server whether this browser actually has a live socket.
     *
     * The pages listen on a private channel AND carry a `wire:poll` fallback, and running both
     * is waste: a working socket already delivers every change, so the poll is thirty seconds
     * of queries that find nothing. Running NEITHER is worse — a workspace with no broadcaster
     * configured, a blocked websocket, a laptop on hotel wifi, and the page silently stops
     * telling the truth.
     *
     * So the client says which world it is in, and the server decides whether to poll. Assume
     * the socket works until proven otherwise: a page that starts by polling and stops is
     * cheaper to be wrong about than one that starts silent.
     */
    window.Alpine.data('liveFallback', () => ({
        /**
         * Whether the right-hand rail is showing.
         *
         * Lives here because the run page's root already owns an x-data, and a second one on
         * the same element replaces the first rather than merging with it.
         */
        railOpen: true,

        setRail(open) {
            this.railOpen = open;
            localStorage.setItem('runbook-run-rail', open ? 'open' : 'closed');
        },

        init() {
            // Narrow screens start closed: the rail is a drawer over the checklist there, and
            // opening over the steps before anybody asks is worse than not opening at all.
            const remembered = localStorage.getItem('runbook-run-rail');
            this.railOpen = remembered
                ? remembered !== 'closed'
                : ! window.matchMedia('(max-width: 999px)').matches;

            const report = (live) => {
                if (this._last === live) return;
                this._last = live;
                this.$wire?.set?.('liveSocket', live, false);
            };

            const connection = window.Echo?.connector?.pusher?.connection;

            if (!window.Echo || !connection) {
                report(false);

                return;
            }

            report(connection.state === 'connected');
            ['connected', 'unavailable', 'failed', 'disconnected', 'connecting'].forEach((state) => {
                connection.bind(state, () => report(connection.state === 'connected'));
            });
        },
    }));

    window.Alpine.data('roomPresence', (config = {}) => ({
        others: {},
        channel: null,
        me: Number(config.me || 0),

        get people() {
            return Object.values(this.others);
        },

        /**
         * How many faces to draw before collapsing the rest into a "+N".
         *
         * Four circles is a header decoration; eleven is a wall. The ones past the limit are
         * not hidden — they move into the list behind the chip, which is the only place that
         * can show a name and what each person is looking at anyway.
         */
        limit: 3,

        open: false,

        get visible() {
            return this.people.slice(0, this.limit);
        },

        get overflow() {
            return Math.max(0, this.people.length - this.limit);
        },

        /** What to say under a name: the step they are on, or simply that they are here. */
        doing(person) {
            return person.focus ? `on ${person.focus}` : 'here now';
        },

        init() {
            if (!window.Echo || !config.room) return;

            this.channel = window.Echo.join(`room-presence.${config.room}`);
            this.channel
                .here((members) => {
                    this.others = {};
                    (members || []).forEach((member) => this.remember(member));
                    this.announce();
                })
                .joining((member) => {
                    this.remember(member);
                    // Announce again so whoever just arrived learns where we are; presence
                    // state is not replayed, so a silent join sees everyone as focused nowhere.
                    this.announce();
                })
                .leaving((member) => {
                    delete this.others[member?.id];
                    this.others = { ...this.others };
                    if (this.overflow === 0) this.open = false;
                })
                .listenForWhisper('focus', (event) => {
                    if (!event || !this.others[event.id]) return;
                    this.others[event.id].focus = event.focus ?? null;
                    this.others = { ...this.others };
                });

            if (config.focus) {
                this.$watch(`$wire.${config.focus}`, () => this.announce());
            }

            // Typing rides the presence channel this component already owns, fanned out as
            // window events — the chat panel is Blade and opening a second subscription would
            // mean two lifecycles that have to agree. Same bridge the canvas island uses.
            this._typing = () => this.channel?.whisper?.('typing', { userId: this.me, name: config.name || 'Someone' });
            window.addEventListener('room-chat-typing', this._typing);
            this.channel.listenForWhisper('typing', (event) => {
                if (Number(event?.userId) === this.me) return;
                window.dispatchEvent(new CustomEvent('room-chat-typing-seen', { detail: event }));
            });
        },

        remember(member) {
            if (!member || Number(member.id) === this.me) return;
            this.others[member.id] = {
                id: member.id,
                name: member.name || 'Someone',
                color: member.color || '#6366f1',
                focus: null,
            };
        },

        announce() {
            if (!config.focus) return;
            this.channel?.whisper?.('focus', {
                id: this.me,
                focus: this.$wire?.get?.(config.focus) ?? null,
            });
        },

        initials(name) {
            return String(name || '?')
                .trim().split(/\s+/).slice(0, 2)
                .map((part) => part[0] || '')
                .join('')
                .toUpperCase() || '?';
        },

        destroy() {
            window.removeEventListener('room-chat-typing', this._typing);
            if (config.room) window.Echo?.leave?.(`room-presence.${config.room}`);
        },
    }));

    window.Alpine.data('incidentTimer', (initialSeconds = 0, running = false) => ({
        seconds: Number(initialSeconds || 0),
        running: Boolean(running),
        interval: null,

        init() {
            if (this.running) {
                this.interval = window.setInterval(() => { this.seconds += 1; }, 1000);
            }
        },

        label() {
            const hours = Math.floor(this.seconds / 3600);
            const minutes = Math.floor((this.seconds % 3600) / 60);
            const seconds = this.seconds % 60;
            return [hours, minutes, seconds].map((value) => String(value).padStart(2, '0')).join(':');
        },

        destroy() {
            if (this.interval) {
                window.clearInterval(this.interval);
                this.interval = null;
            }
        },
    }));

    /*
     * The "?" beside a field label, and the panel of guidance it opens.
     *
     * Teleported for the same reason the select menu is — inside a draggable floating panel the
     * popover's viewport coordinates were being resolved against the PANEL's transform, putting
     * it hundreds of pixels away and then clipping what was left. Teleporting to <body> fixed
     * that and broke the modals: a Flux modal is a <dialog> opened with showModal(), so it sits
     * in the browser's top layer and a body-parked popover renders behind it. Present, correct,
     * and invisible.
     *
     * So the host is chosen the same way the menu chooses it, rather than assumed.
     * @see ui/teleported-panel.js
     */
    window.Alpine.data('fieldHelp', () => ({
        open: false,

        toggle() {
            this.open ? this.close() : this.openPanel();
        },
        openPanel() {
            this.open = true;
            // $nextTick: x-show has to un-hide the panel before it can be measured.
            this.$nextTick(() => this.place());
        },
        close() {
            this.open = false;
        },
        place() {
            const btn = this.$refs.helpButton;
            const panel = this.$refs.helpPanel;

            if (! btn || ! reattach(panel, hostFor(btn, { needsWireScope: false }))) {
                return;
            }

            const r = btn.getBoundingClientRect();
            const h = panel.offsetHeight || 200;
            // Below the question mark, unless that would run off the bottom and there is room
            // above. A tooltip half off-screen is the same failure as one behind the modal.
            const flipUp = r.bottom + 6 + h > window.innerHeight && r.top - h - 6 > 0;

            pinToViewport(panel, {
                top: flipUp ? r.top - h - 6 : r.bottom + 6,
                // Nudged left when the panel's own width would take it past the right edge.
                left: Math.max(8, Math.min(r.left, window.innerWidth - (panel.offsetWidth || 320) - 8)),
                zIndex: 10000,
            });
        },
    }));

    window.Alpine.data('selectSearch', () => ({
        open: false,
        q: '',
        _reposition: null,
        _remorphed: null,
        _panelClosed: null,

        /*
         * The morph listener lives as long as the component, not as long as the menu is open.
         *
         * Registered on open and torn down on close, it missed every re-render that landed
         * while the menu was CLOSED — and that is the common case, because picking an option
         * closes the menu and only then triggers the round trip.
         */
        init() {
            this._remorphed = () => this.settleAfterMorph();
            window.addEventListener('select-search:remorphed', this._remorphed);
        },
        toggle() {
            this.open ? this.close() : this.openPanel();
        },
        openPanel() {
            this.open = true;
            this.q = '';
            this.$nextTick(() => {
                this.place();
                this.$refs.search && this.$refs.search.focus();
            });
            this._reposition = () => this.open && this.place();
            window.addEventListener('scroll', this._reposition, true);
            window.addEventListener('resize', this._reposition);

            // A floating panel closing takes this menu with it — otherwise the menu stays
            // "open" in state while invisible on screen, and the next click on the trigger
            // toggles it shut instead of opening it. Only when the panel actually contains
            // the trigger: another panel closing elsewhere is none of this menu's business.
            this._panelClosed = (event) => {
                if (event.detail?.panel && event.detail.panel.contains(this.$refs.trigger)) {
                    this.close();
                }
            };
            window.addEventListener('floating-panel:closed', this._panelClosed);
        },
        /**
         * A Livewire round trip rebuilt the DOM under this menu.
         *
         * Across two animation frames, not one tick: the morph runs inside a MutationObserver
         * callback and Alpine's own work settles after it, so at the moment the hook fires the
         * page is still moving. Placing twice is cheap and removes the race.
         */
        settleAfterMorph() {
            // Whether the person was typing when the round trip landed. The panel's contents
            // are rewritten underneath them, so without this every keystroke in the search box
            // throws the caret out of the field.
            const wasTyping = this.open && this.$refs.search
                && document.activeElement === this.$refs.search;
            const caret = wasTyping ? this.$refs.search.selectionStart : null;

            const settle = () => {
                if (! this.open) {
                    return;
                }
                this.place();
                if (wasTyping && this.$refs.search && document.activeElement !== this.$refs.search) {
                    this.$refs.search.focus();
                    if (caret !== null) {
                        this.$refs.search.setSelectionRange(caret, caret);
                    }
                }
            };

            requestAnimationFrame(() => {
                settle();
                requestAnimationFrame(settle);
            });
        },
        close() {
            this.open = false;
            this.q = '';
            this.teardown();
        },
        // The panel is teleported to <body>, so it is no longer a descendant of the element
        // click-outside watches — without this, clicking the search box or an option would
        // close the menu out from under the pointer.
        closeUnlessInPanel(event) {
            const panel = this.$refs.panel;
            if (panel && event?.target && panel.contains(event.target)) {
                return;
            }
            this.close();
        },
        teardown() {
            if (this._reposition) {
                window.removeEventListener('scroll', this._reposition, true);
                window.removeEventListener('resize', this._reposition);
                this._reposition = null;
            }
            if (this._panelClosed) {
                window.removeEventListener('floating-panel:closed', this._panelClosed);
                this._panelClosed = null;
            }
        },
        destroy() {
            this.teardown();

            // Registered for the component's lifetime in init(), so it is released here
            // rather than on close.
            if (this._remorphed) {
                window.removeEventListener('select-search:remorphed', this._remorphed);
                this._remorphed = null;
            }
        },
        /*
         * Where the panel has to LIVE, which is a different question from where it is drawn.
         *
         * It is teleported to <body> so it escapes the transforms and overflow clipping of
         * whatever it was declared inside. But a <dialog> opened with showModal() lives in the
         * browser's TOP LAYER, which sits above every z-index in the document — so a panel in
         * <body> renders behind an open modal and only appears once the modal is closed.
         *
         * So: inside an open dialog, the panel belongs in that dialog.
         */
        // Extracted so the choice is unit-tested: parking the clone in the wrong place is
        // invisible until a morph corrupts the page around it. @see ui/teleported-panel.js
        host() {
            return hostFor(this.$refs.trigger);
        },
        // Pin the panel to the trigger's rect; flip above when there's no room below.
        place() {
            const btn = this.$refs.trigger;
            const panel = this.$refs.panel;
            if (!btn || !panel) {
                return;
            }

            /*
             * Put the panel back before measuring it — it may not be in the page at all.
             *
             * A morph deletes a teleported clone parked inside the component's tree, which a
             * menu in a modal has to be. The element survives as an orphan: intact, still
             * carrying this component's scope, still what $refs.panel points at, just not in
             * the document. Re-attaching costs one appendChild and beats waiting for Alpine to
             * build a replacement. The `morph.removing` guard above stops this happening in
             * the first place; this heals a clone that predates it. @see ui/teleported-panel.js
             */
            if (! reattach(panel, this.host())) {
                return;
            }

            const r = btn.getBoundingClientRect();
            const h = Math.min(panel.scrollHeight || 260, 260);
            const flipUp = r.bottom + 4 + h > window.innerHeight && r.top - h - 4 > 0;

            pinToViewport(panel, {
                top: flipUp ? r.top - h - 4 : r.bottom + 4,
                left: r.left,
                minWidth: r.width,
                zIndex: 10000,
            });
        },
    }));

    /* Live-renders a streaming AI reply: Livewire appends raw deltas into a
       hidden buffer ($refs.raw via wire:stream); we re-render it as formatted
       markdown into $refs.out on every mutation, and flip `thinking` off once
       the first token lands (drives the three-dots indicator). */
    window.Alpine.data('streamRender', () => ({
        thinking: true,
        acts: [],
        obs: null,
        actObs: null,
        init() {
            const raw = this.$refs.raw;
            const out = this.$refs.out;
            const render = () => {
                const txt = raw.textContent || '';
                this.thinking = txt.trim().length === 0;
                out.innerHTML = window.zaiMarkdown(txt);
            };
            this.obs = new MutationObserver(render);
            this.obs.observe(raw, { childList: true, characterData: true, subtree: true });
            render();

            // Live tool-activity log: each line streamed to the "activity"
            // channel becomes a step in a small action log (e.g. URLs scraped).
            const actRaw = this.$refs.actRaw;
            if (actRaw) {
                const renderActs = () => {
                    this.acts = (actRaw.textContent || '').split('\n').map((l) => l.trim()).filter(Boolean);
                };
                this.actObs = new MutationObserver(renderActs);
                this.actObs.observe(actRaw, { childList: true, characterData: true, subtree: true });
                renderActs();
            }
        },
        destroy() {
            this.obs && this.obs.disconnect();
            this.actObs && this.actObs.disconnect();
        },
    }));

    /* Visitor analytics charts (Site settings → Visitors). Registered on Alpine —
       NOT an inline blade <script> — because Livewire tab morphs never execute
       injected script tags, which left window.zarVisitorCharts undefined and the
       graphs empty. Chart.js stays lazy-loaded so it only ships on this tab. */
    window.Alpine.data('zarVisitorCharts', (initial) => ({
        charts: {},
        init() {
            this.load().then(() => this.render(initial));
        },
        load() {
            return new Promise((res) => {
                if (window.Chart) return res();
                const s = document.createElement('script');
                s.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js';
                s.onload = res;
                document.head.appendChild(s);
            });
        },
        update(data) {
            if (window.Chart && data) this.render(data);
        },
        render(d) {
            this.draw('zc-daily', this.cfgLine(d.dailyVisits));
            this.draw('zc-countries', this.cfgBar(d.topCountries));
            this.draw('zc-devices', this.cfgDoughnut(d.deviceBreakdown));
            this.draw('zc-browsers', this.cfgDoughnut(d.browserBreakdown));
        },
        draw(id, cfg) {
            const el = document.getElementById(id);
            if (!el) return;
            if (this.charts[id]) this.charts[id].destroy();
            this.charts[id] = new window.Chart(el, cfg);
        },
        destroy() {
            Object.values(this.charts).forEach((c) => c && c.destroy());
            this.charts = {};
        },
        palette: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#a3a3a3'],
        cfgLine(rows) {
            return {
                type: 'line',
                data: {
                    labels: rows.map((r) => r.date),
                    datasets: [
                        { label: 'Visits', data: rows.map((r) => r.visits), borderColor: '#3b82f6', backgroundColor: 'rgba(59,130,246,.1)', fill: true, tension: 0.3 },
                        { label: 'Unique', data: rows.map((r) => r.unique), borderColor: '#10b981', fill: false, tension: 0.3 },
                    ],
                },
                options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } },
            };
        },
        cfgBar(rows) {
            return {
                type: 'bar',
                data: { labels: rows.map((r) => r.label), datasets: [{ data: rows.map((r) => r.value), backgroundColor: '#3b82f6' }] },
                options: { indexAxis: 'y', responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } },
            };
        },
        cfgDoughnut(rows) {
            return {
                type: 'doughnut',
                data: { labels: rows.map((r) => r.label), datasets: [{ data: rows.map((r) => r.value), backgroundColor: this.palette }] },
                options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } },
            };
        },
    }));

    /* Compact project-aware charts for the workspace dashboard. The component is
       recreated when its Livewire key changes, so a project switch or fresh rollup
       paints current data without retaining stale Chart instances. */
    window.Alpine.data('zarDashboardCharts', (data) => ({
        charts: [],
        init() {
            this.load().then(() => this.render());
        },
        load() {
            return new Promise((resolve) => {
                if (window.Chart) return resolve();
                const existing = document.querySelector('script[data-zar-chartjs]');
                if (existing) {
                    existing.addEventListener('load', resolve, { once: true });
                    return;
                }
                const script = document.createElement('script');
                script.dataset.zarChartjs = 'true';
                script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js';
                script.onload = resolve;
                document.head.appendChild(script);
            });
        },
        render() {
            const dark = document.documentElement.classList.contains('dark');
            const grid = dark ? 'rgba(255,255,255,.07)' : 'rgba(24,24,27,.08)';
            const text = dark ? '#a1a1aa' : '#71717a';
            this.charts.push(new window.Chart(this.$refs.trend, {
                type: 'line',
                data: {
                    labels: data.daily.map((row) => row.date),
                    datasets: [
                        { label: 'Visits', data: data.daily.map((row) => row.visits), borderColor: '#4f46e5', backgroundColor: 'rgba(79,70,229,.12)', fill: true, tension: 0.35, pointRadius: 0, borderWidth: 2 },
                        { label: 'Unique', data: data.daily.map((row) => row.unique), borderColor: '#06b6d4', tension: 0.35, pointRadius: 0, borderWidth: 2 },
                    ],
                },
                options: { responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false }, plugins: { legend: { position: 'bottom', labels: { color: text, boxWidth: 10, usePointStyle: true } } }, scales: { x: { grid: { display: false }, ticks: { color: text, maxTicksLimit: 8 } }, y: { beginAtZero: true, grid: { color: grid }, ticks: { color: text, precision: 0 } } } },
            }));
            if (data.devices.length) {
                this.charts.push(new window.Chart(this.$refs.devices, {
                    type: 'doughnut',
                    data: { labels: data.devices.map((row) => row.label), datasets: [{ data: data.devices.map((row) => row.value), backgroundColor: ['#4f46e5', '#06b6d4', '#84cc16', '#f59e0b'], borderWidth: 0 }] },
                    options: { responsive: true, maintainAspectRatio: false, cutout: '68%', plugins: { legend: { position: 'right', labels: { color: text, boxWidth: 8, usePointStyle: true } } } },
                }));
            }
        },
        destroy() {
            this.charts.forEach((chart) => chart.destroy());
        },
    }));
});

window.addEventListener('DOMContentLoaded', () => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Scroll-reveal (declarative, via data-aos="...").
    AOS.init({ duration: 700, easing: 'ease-out-cubic', once: true, disable: reduced });

    if (reduced) return;

    // Buttery smooth scrolling. Don't hijack the wheel when the pointer is over a
    // nested scrollable area (or one tagged data-lenis-prevent) — so inner
    // scrollbars (modals, dropdowns, panels, the chat thread) scroll natively.
    const lenis = new Lenis({
        lerp: 0.1,
        smoothWheel: true,
        prevent: (node) => {
            let el = node;
            while (el && el !== document.body && el !== document.documentElement) {
                if (el.nodeType === 1) {
                    if (el.hasAttribute('data-lenis-prevent')) {
                        return true;
                    }
                    const oy = getComputedStyle(el).overflowY;
                    if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) {
                        return true;
                    }
                }
                el = el.parentElement;
            }
            return false;
        },
    });
    const raf = (time) => {
        lenis.raf(time);
        requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);

    // Motion One: staggered entrance for anything marked [data-stagger] > *.
    document.querySelectorAll('[data-stagger]').forEach((group) => {
        inView(
            group,
            () => {
                animate(
                    group.children,
                    { opacity: [0, 1], transform: ['translateY(24px)', 'translateY(0)'] },
                    { delay: stagger(0.08), duration: 0.6, easing: [0.34, 1.56, 0.64, 1] }
                );
            },
            { amount: 0.2 }
        );
    });

    // GSAP: subtle parallax drift on [data-parallax] as you scroll.
    document.querySelectorAll('[data-parallax]').forEach((el) => {
        const speed = parseFloat(el.dataset.parallax) || 0.2;
        window.addEventListener('scroll', () => {
            gsap.to(el, { y: window.scrollY * speed, duration: 0.4, ease: 'power2.out' });
        });
    });
});
